/**
 * portalActivityLog.js
 *
 * Per-user behavioural log for the client portal (/portal/*), mirroring the
 * Start-side startActivityLog.js. Answers "what did THIS client (or team
 * member) do in the portal, and when" so the CRM Client overview → Access tab
 * can show logins and page views per person.
 *
 * Append-only: one doc per occurrence.
 *
 *   users/{uid}/portalActivity/{autoId}
 *   { kind, key, label, path, at, location? }
 *
 * `location` is IP-derived ({ ip, city, region, country, org }) — see ipLocation.js.
 *
 * Writes are best-effort: tracking must never break the page the client is on.
 */

import { addDoc, collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import { getIpLocation } from './ipLocation';

export const PORTAL_ACTIVITY_COLLECTION = 'portalActivity';

// Most recent entries pulled into the CRM Access tab for one user.
const PORTAL_ACTIVITY_READ_LIMIT = 300;

// ---------------------------------------------------------------------------
// Page labels
// ---------------------------------------------------------------------------

/**
 * Friendly labels for /portal routes, keyed by the path with the /portal
 * prefix stripped ('' = the dashboard index). Paths not in this map fall back
 * to a prettified version of the URL so no visit goes unrecorded.
 */
const PORTAL_PAGE_LABELS = {
  '': 'Dashboard',
  'kickoff': 'Kickoff',
  'profile': 'My Profile',
  'tasks': 'Tasks',
  'messages': 'Messages',
  'calendar': 'Calendar',
  'portal-calendar': 'Calendar',
  'bookonsite': 'Book On-Site Visit',
  'bookcqc': 'Book CQC Visit',
  'my-questions': 'My Questions',
  'question': 'Question detail',
  'fires': 'Fires',
  'fire': 'Fire detail',
  'goals': 'Goals',
  'roadmap': 'Roadmap',
  'billing': 'Billing',
  'programdetail': 'Program details',
  'customprogram': 'Custom program builder',
  'proposal': 'Proposal builder',
  'program-call': 'Program call',
  'checkout': 'Checkout',
  'contract': 'Contract',
  'courses': 'Courses',
  'community': 'Community',
  'collection': 'Collection',
  'takeoffs': 'Takeoffs',
  'conference': 'Conference',
  'summit': 'BBA Summit',
  'jobtread': 'JobTread',
  'kpis': 'KPIs',
  'sops': 'SOPs',
  'job-descriptions': 'Job Descriptions',
  'org-chart': 'Org Chart',
  'proposed-org-chart': 'Proposed Org Chart',
  'my-mission-vision-values': 'Mission, Vision & Values',
  'whats-new': "What's New",

  // Department hubs and their tabs
  'operations': 'Operations',
  'operations/all': 'Operations',
  'operations/sops': 'Operations — SOPs',
  'operations/kpis': 'Operations — KPIs',
  'operations/job-descriptions': 'Operations — Job Descriptions',
  'operations/org-chart': 'Operations — Org Chart',
  'operations/goals': 'Operations — Goals',

  'sales': 'Sales',
  'sales/analytics': 'Sales — Analytics',
  'sales/resources': 'Sales — Resources',
  'sales/goals': 'Sales — Goals',
  'sales/proposals': 'Sales — Proposals',
  'sales/design-pricing': 'Sales — Design Pricing',
  'sales/build-payment-calculator': 'Sales — Build Payment Calculator',

  'finance': 'Finance',
  'finance/all': 'Finance',
  'finance/materials': 'Finance — Materials',
  'finance/analytics': 'Finance — Analytics',
  'finance/goals': 'Finance — Goals',
  'finance/margin': 'Finance — Margin Calculator',
  'finance/overhead-calculator': 'Finance — Overhead Calculator',
  'finance/labor-burden': 'Finance — Labor Burden',

  'marketing': 'Marketing',
  'marketing/all': 'Marketing',
  'marketing/analytics': 'Marketing — Analytics',
  'marketing/goals': 'Marketing — Goals',
  'marketing/website': 'Marketing — Website',
  'marketing/reviews': 'Marketing — Reviews',
  'marketing/calculators': 'Marketing — Calculators',
  'marketing/lead-roi': 'Marketing — Lead ROI',
  'marketing/strategy': 'Marketing — Strategy',

  'technology': 'Technology',
  'technology/all': 'Technology',
  'technology/goals': 'Technology — Goals',
  'technology/tech-stack': 'Technology — Tech Stack',
  'technology/automation': 'Technology — Automation',
  'technology/apps': 'Technology — Apps',
  'technology/receptionist': 'Technology — Receptionist',
  'technology/chatbot': 'Technology — Chatbot',
  'technology/jtformulas': 'Technology — JobTread Formulas',
  'technology/jobtread-imports': 'Technology — JobTread Imports',
  'technology/workflows': 'Technology — Workflows',
  'technology/agents': 'Technology — Agents',
  'technology/claude-skills': 'Technology — Skills',
  'technology/all-software': 'Technology — All Software',

  'hr': 'HR',
  'hr/all': 'HR',
  'hr/materials': 'HR — Materials',
  'hr/hiring': 'HR — Hiring',
  'hr/company-direction': 'HR — Company Direction',
  'hr/goals': 'HR — Goals',
  'hr/onboarding': 'HR — Onboarding',
  'hr/company-policy': 'HR — Company Policy',
  'hr/job-listings': 'HR — Job Listings',
  'hr/interview-evaluation': 'HR — Interview Evaluation',
  'hr/employee-materials': 'HR — Employee Materials',
};

/**
 * Payment/signing callback pages the user never chose to visit — logging them
 * would be noise between the page they left and the page they land on.
 */
const SKIPPED_PORTAL_PATHS = new Set([
  'checkout/return',
  'checkout/strategy-calls/return',
  'contract-complete',
  'bid-contract-complete',
  'nda',
]);

/** Strip the /portal prefix and any trailing slash so a path can hit the label map. */
function normalizePortalPath(pathname) {
  if (typeof pathname !== 'string') return null;
  if (!pathname.startsWith('/portal')) return null;
  return pathname
    .slice('/portal'.length)
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')
    .toLowerCase();
}

/** 'hr/hiring/position/abc123' → 'hr/hiring/position' (doc ids aren't labels). */
function stripIdSegments(key) {
  return key
    .split('/')
    .filter((seg) => !/^[A-Za-z0-9_-]{15,}$/.test(seg) && !/^\d+$/.test(seg))
    .join('/');
}

/** 'proposed-org-chart' → 'Proposed Org Chart' — fallback for unmapped paths. */
function prettifySegment(segment) {
  return segment
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Human label for a /portal path, or null when the page shouldn't be logged.
 * @param {string} pathname
 * @returns {string|null}
 */
export function resolvePortalPageLabel(pathname) {
  const key = normalizePortalPath(pathname);
  if (key == null) return null;
  if (SKIPPED_PORTAL_PATHS.has(key)) return null;

  if (PORTAL_PAGE_LABELS[key] !== undefined) return PORTAL_PAGE_LABELS[key];

  const withoutIds = stripIdSegments(key);
  if (PORTAL_PAGE_LABELS[withoutIds] !== undefined) return PORTAL_PAGE_LABELS[withoutIds];

  // Unknown page — still worth a row. Prettify: 'hr/some-new-page' → 'HR — Some New Page'
  const segments = (withoutIds || key).split('/').filter(Boolean);
  if (!segments.length) return 'Dashboard';
  const first = PORTAL_PAGE_LABELS[segments[0]] || prettifySegment(segments[0]);
  const rest = segments.slice(1).map(prettifySegment).join(' / ');
  return rest ? `${first} — ${rest}` : first;
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

// A remount or a StrictMode double-effect would otherwise write the same visit
// twice within milliseconds. Kept short so genuine back-and-forth navigation
// still lands as separate rows.
const DUPLICATE_WINDOW_MS = 3000;
const lastWriteByKey = new Map();

function isDuplicate(uid, key) {
  const cacheKey = `${uid}:${key}`;
  const now = Date.now();
  const previous = lastWriteByKey.get(cacheKey);
  if (previous && now - previous < DUPLICATE_WINDOW_MS) return true;
  lastWriteByKey.set(cacheKey, now);
  return false;
}

/**
 * Log a portal page visit. No-ops for paths outside /portal and for
 * callback/return pages.
 * @param {object} user - Firebase auth user (needs .uid)
 * @param {string} pathname
 */
export async function logPortalPageView(user, pathname) {
  const uid = user?.uid;
  if (!uid) return;
  const label = resolvePortalPageLabel(pathname);
  if (!label) return;

  const key = `page_${normalizePortalPath(pathname) || 'dashboard'}`;
  if (isDuplicate(uid, key)) return;

  // Timestamp the visit before the (cached, usually instant) IP lookup
  const at = new Date().toISOString();
  const location = await getIpLocation();

  try {
    await addDoc(collection(db, 'users', uid, PORTAL_ACTIVITY_COLLECTION), {
      kind: 'page_view',
      key,
      label,
      path: pathname,
      at,
      ...(location ? { location } : {}),
    });
  } catch (error) {
    console.warn('[portalActivity] failed to log', key, error);
  }
}

// ---------------------------------------------------------------------------
// Reads (CRM side)
// ---------------------------------------------------------------------------

/**
 * Load a portal user's activity, newest first.
 * Returns [] rather than throwing so the Client overview still renders.
 *
 * @param {string} userId - the portal user's Firebase UID
 * @returns {Promise<Array<object>>}
 */
export async function fetchPortalActivity(userId) {
  if (!userId) return [];
  try {
    const snap = await getDocs(
      query(
        collection(db, 'users', userId, PORTAL_ACTIVITY_COLLECTION),
        orderBy('at', 'desc'),
        limit(PORTAL_ACTIVITY_READ_LIMIT)
      )
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.warn('[portalActivity] failed to load activity for', userId, error);
    return [];
  }
}
